import { useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { ApiError, api } from "@client/api/client";
import { Button, Card, PageHeader, Spinner } from "@client/components/ui";
import { useLocalizedText } from "@client/components/Localized";
import { useLang, useT } from "@client/i18n";
import { formatDayShort } from "@client/lib/format";
import { DEFAULT_TIMEZONE, dayKey } from "@shared/time";
import { useStartPractice } from "./api";
import { createSession, saveSession } from "./session";
import type { PracticeQuestionsResponse } from "./types";

export function InvalidPracticeLink() {
  const t = useT();
  return (
    <div>
      <p role="alert">{t("practice.link.invalid")}</p>
      <Link className="underline" to="/practice">
        {t("practice.link.reset")}
      </Link>
    </div>
  );
}

export function LectureSetup({ course, lecture }: { course: string; lecture: string }) {
  const t = useT();
  const { lang } = useLang();
  const localize = useLocalizedText();
  const navigate = useNavigate();
  const start = useStartPractice();
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const query = useQuery({
    queryKey: ["practice", "lecture", course, lecture],
    queryFn: () =>
      api.get<PracticeQuestionsResponse>(
        `/api/practice/questions?${new URLSearchParams({ course, lecture })}`,
      ),
  });
  const identity = query.data?.meta.lecture;
  async function begin() {
    const result = await start.mutateAsync({ course, lecture });
    if (!mounted.current || !result.questions.length || !result.meta.lecture) return;
    saveSession({ ...createSession("topic", result.questions), lecture: result.meta.lecture });
    navigate("/practice/session");
  }
  if (query.isLoading) return <Spinner label={t("common.loading")} />;
  if (query.isError)
    return (
      <div>
        <p role="alert">
          {query.error instanceof ApiError && query.error.status === 404
            ? t("practice.lecture.notFound")
            : t("practice.lecture.error")}
        </p>
        <Link className="underline" to="/practice">
          {t("practice.link.reset")}
        </Link>
      </div>
    );
  if (!identity) return <InvalidPracticeLink />;
  const empty = !query.data?.questions.length;
  // P1-12: the lecture's questions are not open to students yet.
  const release = query.data?.meta.release;
  const locked = release?.open === false;
  return (
    <div className="mx-auto max-w-2xl">
      <PageHeader
        title={`${identity.id} · ${localize(identity.title)}`}
        description={identity.course}
      />
      <Card>
        <ul className="mb-4 list-disc space-y-2 pl-5">
          {identity.objectives.map((objective, i) => (
            <li key={i}>{localize(objective)}</li>
          ))}
        </ul>
        {locked ? (
          <p role="status" className="font-medium">
            {release.opensAt !== null
              ? t("practice.lecture.opens", {
                  date: formatDayShort(dayKey(release.opensAt, DEFAULT_TIMEZONE), lang),
                })
              : t("practice.lecture.closed")}
          </p>
        ) : (
          <p>{t("practice.lecture.pool", { count: query.data?.meta.eligible ?? 0 })}</p>
        )}
        {empty && !locked && (
          <p className="my-4">
            {t(
              identity.status === "unpublished"
                ? "practice.lecture.unpublished"
                : "practice.lecture.empty",
            )}
          </p>
        )}
        {start.isError && <p role="alert">{t("practice.lecture.error")}</p>}
        {start.data?.questions.length === 0 && <p role="status">{t("practice.lecture.empty")}</p>}
        <Button
          className="mt-6"
          fullWidth
          disabled={empty}
          loading={start.isPending}
          onClick={() => {
            void begin().catch(() => {});
          }}
        >
          {t("practice.setup.start")}
        </Button>
      </Card>
    </div>
  );
}
